import { useEffect, useLayoutEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TaskDialog,
  TaskTable,
  KanbanViewModel,
  CalendarViewModel,
} from "@/components/project";
import { Plus, Settings as SettingsIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTaskDialog } from "@/contexts/TaskDialogContext";

type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];
type TaskPartialUpdate = Partial<TaskRow> & { id: string };

export function Project() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [tasks, setTasks] = useState<TaskWithDetailsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithDetailsRow | null>(null);
  const [initialStatus, setInitialStatus] = useState<"todo" | "in_progress" | "review" | "done" | undefined>(
    undefined,
  );
  const [view, setView] = useState<"list" | "kanban" | "calendar">("list");
  const [hideCompleted, setHideCompleted] = useState(true);
  const [filterQuery] = useState("");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>("all");
  const hasFetched = { current: false };
  const { taskToOpen, clearTaskToOpen } = useTaskDialog();

  // Open task dialog when taskToOpen is set from context
  // Use useLayoutEffect to ensure it runs synchronously before paint
  useLayoutEffect(() => {
    if (taskToOpen) {
      // If we're on the project page for this task, open the dialog immediately
      if (taskToOpen.project_id === id) {
        setEditingTask(taskToOpen);
        setTaskDialogOpen(true);
        clearTaskToOpen();
      }
      // If we're not on the right project page, the navigation will happen
      // and this effect will run again when we arrive at the project page
    }
  }, [taskToOpen, id, clearTaskToOpen]);

  useEffect(() => {
    if (!id) return;

    let isMounted = true;

    // Fetch tasks with or without completed based on hideCompleted
    // Use tasks_with_details view to get project and assigned user info in one query
    let tasksQuery = supabase.from("tasks_with_details").select("*").eq("project_id", id);

    if (hideCompleted) {
      tasksQuery = tasksQuery.neq("status", "done");
    }

    Promise.all([
      supabase.from("projects").select("*").eq("id", id).single(),
      tasksQuery
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("updated_at", { ascending: false }),
    ]).then(([projectRes, tasksRes]) => {
      if (!isMounted) return;

      if (projectRes.error) {
        setError(projectRes.error.message);
        setLoading(false);
        return;
      }

      if (tasksRes.error) {
        setError(tasksRes.error.message);
      } else {
        setTasks(tasksRes.data ?? []);
      }

      setProject(projectRes.data);
      hasFetched.current = true;
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [id, hideCompleted]);

  const fetchTasks = () => {
    if (!id) return;

    let query = supabase.from("tasks_with_details").select("*").eq("project_id", id);

    if (hideCompleted) {
      query = query.neq("status", "done");
    }

    query.order("created_at", { ascending: false }).then(({ data, error }) => {
      if (error) {
        console.error("Error fetching tasks:", error);
      } else {
        setTasks(data ?? []);
      }
    });
  };

  const handleMarkComplete = async (task: TaskWithDetailsRow) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    const updatedTask: TaskWithDetailsRow = {
      ...task,
      status: newStatus,
      updated_at: new Date().toISOString(),
    };

    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === task.id ? updatedTask : t)));

    const { error } = await supabase
      .from("tasks")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", task.id!);

    if (error) {
      // Revert on error
      setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
      console.error("Error updating task:", error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm("Are you sure you want to delete this task?")) return;

    // Optimistic update
    const taskToDelete = tasks.find((t) => t.id === taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));

    const { error } = await supabase.from("tasks").delete().eq("id", taskId);

    if (error) {
      // Revert on error
      if (taskToDelete) {
        setTasks((prev) => [...prev, taskToDelete]);
      }
      console.error("Error deleting task:", error);
    }
  };

  const handleMoveTask = async (taskId: string, toStatus: "todo" | "in_progress" | "review" | "done") => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updatedTask = { ...task, status: toStatus, updated_at: new Date().toISOString() };

    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)));

    const { error } = await supabase
      .from("tasks")
      .update({ status: toStatus, updated_at: new Date().toISOString() })
      .eq("id", taskId);

    if (error) {
      // Revert on error
      setTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
      console.error("Error moving task:", error);
    }
  };

  const handleTaskCreated = (newTask: TaskWithDetailsRow | TaskPartialUpdate) => {
    // For create, we always receive full TaskWithDetailsRow from TaskDialog
    const taskWithDetails = newTask as TaskWithDetailsRow;

    // Only add if it matches current filters
    if (hideCompleted && taskWithDetails.status === "done") return;
    setTasks((prev) => {
      if (prev.find((t) => t.id === taskWithDetails.id)) return prev;
      return [...prev, taskWithDetails].sort((a, b) => {
        if (a.due_date && b.due_date) {
          return a.due_date.localeCompare(b.due_date);
        }
        return 0;
      });
    });
  };

  const handleTaskUpdated = (updatedTask: TaskWithDetailsRow | TaskPartialUpdate) => {
    // If we received a partial update, merge only the updated fields
    if (!("project_name" in updatedTask) && updatedTask.id) {
      setTasks((prev) => {
        const existingTask = prev.find((t) => t.id === updatedTask.id);
        if (!existingTask) return prev;

        // Merge only the updated fields into existing task (preserve view data like project_name, etc.)
        const mergedTask: TaskWithDetailsRow = {
          ...existingTask,
          ...updatedTask,
        };

        // Update or remove based on filters
        if (hideCompleted && mergedTask.status === "done") {
          return prev.filter((t) => t.id !== mergedTask.id);
        } else {
          return prev.map((t) => (t.id === mergedTask.id ? mergedTask : t));
        }
      });
      return;
    }

    // Update or remove based on filters (updatedTask is already TaskWithDetailsRow)
    const taskWithDetails = updatedTask as TaskWithDetailsRow;
    if (hideCompleted && taskWithDetails.status === "done") {
      setTasks((prev) => prev.filter((t) => t.id !== taskWithDetails.id));
    } else {
      setTasks((prev) => prev.map((t) => (t.id === taskWithDetails.id ? taskWithDetails : t)));
    }
  };

  if (loading && tasks.length === 0 && !hasFetched.current) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Loading...</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-muted-foreground">Loading project…</div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Error</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-destructive">{error || "Project not found"}</div>
          <Button onClick={() => navigate("/calendar")} className="mt-4">
            Back to Calendar
          </Button>
        </div>
      </div>
    );
  }

  // Extract unique assignees from tasks for filter options
  const uniqueAssignees = Array.from(
    new Map(
      tasks
        .filter((t) => t.assigned_to)
        .map((t) => [
          t.assigned_to,
          {
            id: t.assigned_to,
            username: t.assigned_username || t.assigned_to,
            full_name: t.assigned_full_name,
          },
        ]),
    ).values(),
  );

  const filteredTasks = tasks.filter((t) => {
    if (hideCompleted && t.status === "done") return false;

    // Assignee filter
    if (selectedAssigneeId !== "all" && t.assigned_to !== selectedAssigneeId) {
      return false;
    }

    if (filterQuery.trim().length > 0) {
      const q = filterQuery.trim().toLowerCase();
      return (
        t.title?.toLowerCase().includes(q) ||
        (t.description ? t.description.toLowerCase().includes(q) : false) ||
        (t.tags ? t.tags.some((tag) => tag.toLowerCase().includes(q)) : false)
      );
    }
    return true;
  });

  const todoTasks = filteredTasks.filter((t) => t.status === "todo");
  const inProgressTasks = filteredTasks.filter((t) => t.status === "in_progress");
  const reviewTasks = filteredTasks.filter((t) => t.status === "review");
  const doneTasks = filteredTasks.filter((t) => t.status === "done");

  return (
    <>
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">{project.name}</h1>
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            setEditingTask(null);
            setInitialStatus(undefined);
            setTaskDialogOpen(true);
          }}
          aria-label="Add task"
        >
          <Plus className="h-4 h-4 md:mr-2" />
          <span className="hidden md:inline">Add task</span>
        </Button>
      </header>
      <main className={`flex-1 overflow-y-auto p-6 ${isMobile ? "pb-content-safe" : ""}`}>
        <div className="w-full space-y-4 min-w-0">
          {project.description && <p className="text-muted-foreground">{project.description}</p>}

          {/* Control Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Select value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All assignees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All assignees</SelectItem>
                  {uniqueAssignees.map((assignee) => (
                    <SelectItem key={assignee.id} value={assignee.id!}>
                      {assignee.full_name || assignee.username || "Unnamed"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/project/${id}/settings`)}
                aria-label="Project settings"
              >
                <SettingsIcon className="size-4" />
              </Button>
            </div>
          </div>

          <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="list" className="flex-1 sm:flex-initial">
                  List
                </TabsTrigger>
                <TabsTrigger value="kanban" className="flex-1 sm:flex-initial">
                  Kanban
                </TabsTrigger>
                <TabsTrigger value="calendar" className="flex-1 sm:flex-initial">
                  Calendar
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={hideCompleted}
                    onCheckedChange={(v) => setHideCompleted(Boolean(v))}
                  />
                  <span className="text-muted-foreground whitespace-nowrap">Hide completed</span>
                </div>
              </div>
            </div>

            <TabsContent value="list" className="mt-4">
              <TaskTable
                tasks={filteredTasks}
                onEdit={(task) => {
                  setEditingTask(task);
                  setTaskDialogOpen(true);
                }}
                onMarkComplete={handleMarkComplete}
                onDelete={handleDeleteTask}
                projectId={project.id}
                projectName={project.name}
              />
            </TabsContent>

            <TabsContent value="kanban" className="mt-4">
              <KanbanViewModel
                todoTasks={todoTasks}
                inProgressTasks={inProgressTasks}
                reviewTasks={reviewTasks}
                doneTasks={doneTasks}
                onEdit={(task) => {
                  setEditingTask(task);
                  setTaskDialogOpen(true);
                }}
                onView={(task) => {
                  setEditingTask(task);
                  setTaskDialogOpen(true);
                }}
                onMarkComplete={handleMarkComplete}
                onDelete={handleDeleteTask}
                onMove={handleMoveTask}
                onAddTask={(status) => {
                  setEditingTask(null);
                  setInitialStatus(status);
                  setTaskDialogOpen(true);
                }}
              />
            </TabsContent>

            <TabsContent value="calendar" className="mt-4">
              <CalendarViewModel
                tasks={filteredTasks}
                onView={(task) => {
                  setEditingTask(task);
                  setTaskDialogOpen(true);
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      </div>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) {
            setEditingTask(null);
            setInitialStatus(undefined);
          }
        }}
        onSuccess={(task) => {
          if (task) {
            if (editingTask) {
              handleTaskUpdated(task);
            } else {
              handleTaskCreated(task);
            }
          } else {
            fetchTasks();
          }
          setEditingTask(null);
        }}
        onDelete={handleDeleteTask}
        projectId={project.id}
        task={editingTask}
        mode={editingTask ? "edit" : "create"}
        initialStatus={initialStatus}
      />

      {/* Floating Action Button */}
      <Button
        onClick={() => {
          setEditingTask(null);
          setInitialStatus(undefined);
          setTaskDialogOpen(true);
        }}
        className={`fixed ${isMobile ? "bottom-safe-tab" : "bottom-8"} right-6 md:right-8 h-14 w-14 md:h-16 md:w-16 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-40`}
        size="icon"
        aria-label="Add new task"
      >
        <Plus className="h-6 w-6 md:h-7 md:w-7" />
      </Button>
    </>
  );
}